import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2 } from "lucide-react";

export default function Afkortingen() {
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [form, setForm] = useState({ naam: "", d: "0.0", afkortingen: "", synoniemen: "", toestel: "Mini-trampoline / Pegasus" });
  const [adminErr, setAdminErr] = useState("");

  const load = () => api.get("/reglement/afkortingen").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const filtered = data.filter((d) => {
    if (!q) return true;
    const hay = (d.naam + " " + (d.afkortingen || []).join(" ") + " " + (d.synoniemen || []).join(" ")).toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const byToestel = filtered.reduce((acc, x) => { (acc[x.toestel] = acc[x.toestel] || []).push(x); return acc; }, {});

  const addAfk = async (e) => {
    e.preventDefault();
    setAdminErr("");
    try {
      await api.post("/admin/afkortingen", {
        naam: form.naam,
        d: parseFloat(form.d) || 0,
        afkortingen: form.afkortingen.split(",").map(s => s.trim()).filter(Boolean),
        synoniemen: form.synoniemen.split(",").map(s => s.trim()).filter(Boolean),
        toestel: form.toestel,
      });
      setForm({ naam: "", d: "0.0", afkortingen: "", synoniemen: "", toestel: form.toestel });
      load();
    } catch (e) { setAdminErr(formatError(e)); }
  };

  const delAfk = async (id) => {
    if (!window.confirm("Verwijderen?")) return;
    await api.delete(`/admin/afkortingen/${id}`);
    load();
  };

  return (
    <div data-testid="afkortingen-page">
      <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Naslag</div>
      <h1 className="heading-display text-4xl sm:text-5xl mt-1">Afkortingen & Synoniemen</h1>
      <p className="text-slate-600 mt-3 mb-6">Overzicht van veelgebruikte afkortingen en synoniemen voor sprongen.</p>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Zoek sprong of afkorting..."
        className="w-full max-w-md px-4 py-2.5 border border-input rounded-lg bg-white mb-6 focus:outline-none focus:ring-2 focus:ring-orange-500"
        data-testid="afkortingen-search"
      />

      {isAdmin && (
        <section className="gs-card p-5 mb-6 border-2 border-orange-200 bg-orange-50/30" data-testid="admin-afk-panel">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Plus size={18}/> Nieuwe afkorting toevoegen <span className="text-xs px-2 py-0.5 rounded bg-orange-500 text-white">admin</span></h2>
          <form onSubmit={addAfk} className="grid sm:grid-cols-2 gap-3">
            <input value={form.naam} onChange={e => setForm({...form, naam: e.target.value})} required placeholder="Sprong naam" className="px-3 py-2 border rounded-lg" data-testid="admin-afk-naam"/>
            <input value={form.d} onChange={e => setForm({...form, d: e.target.value})} type="number" step="0.1" placeholder="D-waarde" className="px-3 py-2 border rounded-lg" data-testid="admin-afk-d"/>
            <input value={form.afkortingen} onChange={e => setForm({...form, afkortingen: e.target.value})} placeholder="Afkortingen (komma-gescheiden)" className="px-3 py-2 border rounded-lg" data-testid="admin-afk-afk"/>
            <input value={form.synoniemen} onChange={e => setForm({...form, synoniemen: e.target.value})} placeholder="Synoniemen (komma-gescheiden)" className="px-3 py-2 border rounded-lg" data-testid="admin-afk-syn"/>
            <select value={form.toestel} onChange={e => setForm({...form, toestel: e.target.value})} className="px-3 py-2 border rounded-lg sm:col-span-2" data-testid="admin-afk-toestel">
              <option>Mini-trampoline / Pegasus</option>
              <option>Airtrack / Long-mat</option>
            </select>
            {adminErr && <div className="text-red-600 text-sm sm:col-span-2">{adminErr}</div>}
            <button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 rounded-lg sm:col-span-2" data-testid="admin-afk-submit">Toevoegen</button>
          </form>
        </section>
      )}

      {Object.entries(byToestel).map(([toestel, items]) => (
        <section key={toestel} className="gs-card p-5 mb-6" data-testid={`afk-${toestel}`}>
          <h2 className="text-lg font-bold mb-4">{toestel}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left border-b-2 border-slate-200">
                <tr>
                  <th className="py-2 pr-4 font-semibold">Sprong</th>
                  <th className="py-2 pr-4 font-semibold">D-waarde</th>
                  <th className="py-2 pr-4 font-semibold">Afkortingen</th>
                  <th className="py-2 pr-4 font-semibold">Synoniemen</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-4 font-medium">{it.naam} {it.custom && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">custom</span>}</td>
                    <td className="py-2 pr-4 font-mono">{(it.d ?? 0).toFixed(1)}</td>
                    <td className="py-2 pr-4">{(it.afkortingen || []).map((a, i) => <span key={i} className="gs-chip mr-1">{a}</span>)}</td>
                    <td className="py-2 pr-4 text-slate-600">
                      {(it.synoniemen || []).length === 0 ? "—" : (
                        <span title={(it.synoniemen || []).join(", ")}>
                          {(it.synoniemen || []).slice(0, 6).join(", ")}
                          {(it.synoniemen || []).length > 6 && <span className="text-slate-400"> +{it.synoniemen.length - 6} meer</span>}
                        </span>
                      )}
                    </td>
                    {isAdmin && <td>{it.custom && it.id && <button onClick={() => delAfk(it.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`admin-del-${it.id}`}><Trash2 size={16}/></button>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
